// src/components/more/MySchool.js
import React from 'react';
import { ChevronLeft, Activity, Map } from 'lucide-react';
import { useRequestProcessor } from '../../requestProcessor';
import { useAtom } from 'jotai';
import { activeChildDetail, appConstants } from '../../atomstore/store';
import { fetchCenters, fetchTeachersData } from '../../queryfunctions/queryfunctions';
import SpinnerLoading from '../common/SpinnerLoading';
import { useNavigate } from 'react-router-dom';

const MySchool = () => {
    const navigate = useNavigate();
    const { useQueryWrapper } = useRequestProcessor();
    const [activeChild] = useAtom(activeChildDetail);
    const [constants] = useAtom(appConstants);

    const { data: fetchTeachers, isLoading } = useQueryWrapper(
        ['fetchTeachers', activeChild?.id],
        () => fetchTeachersData()
    );

    const { data: Centers, isFetching } = useQueryWrapper(
        ['Centers', activeChild?.id],
        () => fetchCenters()
    );

    /* -------------------- DESIGNATION SPLIT -------------------- */

    const deputyCentreHeads = fetchTeachers?.filter(
        t => t?.User_Designation?.toLowerCase() === 'deputy centre head'
    );

    const didiTeachers = fetchTeachers?.filter(t => {
        const d = t?.User_Designation?.toLowerCase();
        return d === 'didi' || d === 'kitchen incharge';
    });

    const otherTeachers = fetchTeachers?.filter(t => {
        const d = t?.User_Designation?.toLowerCase();
        return d !== 'center director' &&
            d !== 'centre director' &&
            d !== 'deputy centre head' &&
            d !== 'didi' &&
            d !== 'kitchen incharge';
    });

    /* -------------------- REUSABLE CARD -------------------- */

    const TeacherCard = (teacher, idx) => (
        <div
            key={idx}
            className="flex items-start space-x-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0"
        >
            {teacher?.User_Image ? (
                <img
                    src={constants?.teachersPath + teacher.User_Image}
                    alt={teacher.User_Name}
                    className="w-16 h-16 rounded-xl object-cover object-top flex-shrink-0"
                    onError={(e) => (e.target.style.display = 'none')}
                />
            ) : (
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl">👩🏻‍🏫</span>
                </div>
            )}

            <div className="flex-1">
                <h4 className="font-semibold text-gray-800">{teacher.User_Name}</h4>
                <p className="text-xs text-cyan-600 mb-1">
                    {teacher.User_Designation}
                </p>
                <p className="text-xs text-gray-600">{teacher.desc}</p>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {(isFetching || isLoading) && <SpinnerLoading />}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>My School</h2>
            </div>

            <div className="p-4 space-y-4">
                {/* Center Banner */}
                <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">
                        Footprints {Centers?.Center_Name}
                    </h3>
                    <p className="text-cyan-100 text-sm">
                        Your child's learning home
                    </p>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-3">
                    <button className="bg-white rounded-2xl p-4 shadow-sm border">
                        <div
                            onClick={() => navigate('/profile')}
                            className="w-12 h-12 bg-gradient-to-br from-orange-400 to-pink-400 rounded-xl flex items-center justify-center mx-auto mb-2"
                        >
                            <Activity className="w-6 h-6 text-white" />
                        </div>
                        <p className="text-sm font-semibold text-center">
                            My Profile
                        </p>
                    </button>

                    <button
                        onClick={() =>
                            Centers?.Center_Map_Address &&
                            window.open(Centers.Center_Map_Address, '_blank')
                        }
                        disabled={!Centers?.Center_Map_Address}
                        className={`bg-white rounded-2xl p-4 shadow-sm border ${Centers?.Center_Map_Address
                            ? ''
                            : 'opacity-50 cursor-not-allowed'
                            }`}
                    >
                        <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                            <Map className="w-6 h-6 text-white" />
                        </div>
                        <p className="text-sm font-semibold text-center">
                            Google Maps
                        </p>
                    </button>
                </div>

                {/* Center Details */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border">
                    <h3 className="font-bold mb-3">Center Details</h3>
                    <p className="text-sm text-gray-700">
                        {Centers?.Center_Address}<br />
                        {Centers?.Center_City}, {Centers?.Center_State} ({Centers?.Center_Pincode})
                    </p>
                </div>

                {/* Center Director */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border">
                    <h3 className="font-bold mb-3">Center Director</h3>
                    <div className="flex space-x-3">
                        <img
                            src={constants?.teachersPath + Centers?.Center_Head_Photo}
                            alt=""
                            className="w-20 h-20 rounded-xl object-cover"
                        />
                        <div>
                            <h4 className="font-bold">
                                {Centers?.Center_Head_Name}
                            </h4>
                            <p className="text-xs text-cyan-600">
                                Center Director
                            </p>
                            <p className="text-sm text-gray-600">
                                {Centers?.Center_Head_About}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Team Sections */}
                {deputyCentreHeads?.length > 0 && (
                    <div className="bg-white rounded-2xl p-4 shadow-sm border">

                        <h3 className="font-bold mb-3">Deputy Centre Head</h3>

                        <div className="space-y-4 mb-4">
                            {deputyCentreHeads.map(TeacherCard)}
                        </div>

                    </div>
                )}

                {otherTeachers?.length > 0 && (
                    <div className="bg-white rounded-2xl p-4 shadow-sm border">
                        <h3 className="font-bold mb-3">Teachers</h3>
                        <div className="space-y-4 mb-4">
                            {otherTeachers.map(TeacherCard)}
                        </div>
                    </div>
                )}

                {didiTeachers?.length > 0 && (
                    <div className="bg-white rounded-2xl p-4 shadow-sm border">
                        <h3 className="font-bold mb-3">DIDI</h3>
                        <div className="space-y-4">
                            {didiTeachers.map(TeacherCard)}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default MySchool;
